import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useRouter } from "next/router";
import { isValidToken } from "@utils/isValidToken";
import { Sanity } from "@models/sanity.model";
import { AccountGlobalSchema } from "@models/user-account-globals";
import { ProductModal } from "@models/product-globals.model";
import { ProfileAction, useUserProfile } from "@providers/profile";
import Layout from "@components/layout";
import Account from "@components/account";
import { StateSchema } from "@components/account/address/Address.model";
import * as Sentry from "@sentry/nextjs";
import Sidebar from "@components/account/sidebar";

export interface AccountAuthProps extends Sanity.PageProps {
  pageID: string;
  seo: Sanity.Seo;
  membershipModal?: ProductModal;
  states?: Array<StateSchema>;
  accountGlobals?: AccountGlobalSchema;
}

const AccountAuth = ({ globals, accountGlobals, pageID, seo, ...rest }: AccountAuthProps) => {
  const [loading, setLoading] = useState(true);
  const {
    logout,
    dispatch,
    profileState: { authToken, verificationStatus, profile },
    setIsLoginOpen,
    getAccount,
  } = useUserProfile();
  const { push, pathname } = useRouter();

  const updateAccountVerificationStatus = useCallback(
    async (email: string) => {
      const response = await getAccount(email, true);
      dispatch({ type: ProfileAction.SET_VERIFICATION, verificationStatus: response });
    },
    [getAccount, dispatch]
  );

  useLayoutEffect(() => {
    // Check if user isn't logged in at all.
    if (!authToken.value) {
      setIsLoginOpen(true);
      push({
        pathname: "/",
        query: { from: pathname },
      });
      return;
    }

    // Check if user authToken has expired.
    if (authToken.value && !isValidToken(authToken)) {
      logout(dispatch, { redirect: true });
      return;
    }

    // User is logged in and valid
    Sentry.setUser({ email: profile.email});
    setLoading(false);
  }, []);

  useEffect(() => {
    if (profile?.email) {
      updateAccountVerificationStatus(profile.email);
    }
  }, [updateAccountVerificationStatus, profile.email]);

  return !loading ? (
    <Layout globals={globals} pageID={pageID} hideBanner={false} seo={seo}>
      <Account {...rest} verificationStatus={verificationStatus} accountGlobals={accountGlobals}>
        <Sidebar accountType={verificationStatus?.account_type} />
      </Account>
    </Layout>
  ) : (
    <></>
  );
};

export default AccountAuth;
